import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Contact } from "@/types/contacts";
import { parseContactsCSV, exportContactsToCSV, parseContactsCSVWithMapping } from "@/utils/contactParser";
import { Upload, Download, FileText, Loader2, Plus } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { ContactDashboard } from "@/components/dashboard/ContactDashboard";
import { useUserRole } from "@/hooks/useUserRole";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { generateContactsTemplate, downloadTemplate } from "@/utils/csvTemplates";
import { areNamesEquivalent } from "@/utils/entityNormalizer";
import Papa from 'papaparse';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { checkContactDuplicates } from "@/utils/duplicateChecker";
import { ImportConfirmDialog, ImportMode } from "@/components/ImportConfirmDialog";
import { AddContactDialog } from "@/components/AddContactDialog";
import { recordImportMetadata } from "@/utils/importMetadata";
import { useQueryClient } from "@tanstack/react-query";
import { LastUpdateInfo } from "@/components/dashboard/LastUpdateInfo";
import { ColumnMapperDialog } from "@/components/ColumnMapperDialog";
import { FieldMapping } from "@/utils/columnMapper";

const Contacts = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>("all");
  const [selectedState, setSelectedState] = useState<string>("all");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [selectedCompany, setSelectedCompany] = useState<string>("all");
  const [searchName, setSearchName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  
  // Estados de importação
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState("");
  const [importCategory, setImportCategory] = useState<'restaurant' | 'operator' | 'consolidator'>('restaurant');
  
  // Estados para mapeamento de colunas
  const [showColumnMapper, setShowColumnMapper] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [pendingCsvContent, setPendingCsvContent] = useState<string>('');
  
  // Estados para verificação de duplicação
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [pendingImport, setPendingImport] = useState<{
    new: Contact[];
    existing: Contact[];
    duplicates: Contact[];
  } | null>(null);
  
  // Estado para adicionar contato
  const [showAddDialog, setShowAddDialog] = useState(false);
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { role, isAdmin, isEditor, isCliente, isRsvp, loading: roleLoading } = useUserRole();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const canEdit = isAdmin || isEditor || isCliente || isRsvp;

  // Check authentication and redirect if needed
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        const currentPath = `${location.pathname}${location.search}`;
        navigate("/login", { state: { from: currentPath } });
      }
    };
    checkAuth();
  }, [navigate, location]);

  useEffect(() => {
    // Wait for role to be loaded before fetching
    if (!roleLoading) {
      fetchContacts();
    }
    
    // Check if there's a company filter from navigation
    const company = searchParams.get('company');
    if (company) {
      setSelectedCompany(company);
    }
  }, [searchParams, roleLoading]);

  useEffect(() => {
    filterContacts();
    
    // Log search activity when filters are applied (debounced via useEffect)
    if (selectedRegion !== 'all' || selectedState !== 'all' || selectedType !== 'all' || 
        selectedCompany !== 'all' || searchName.trim()) {
      const logSearch = async () => {
        const { logContactAccess } = await import('@/utils/contactAccessLogger');
        logContactAccess({
          contactCount: filteredContacts.length,
          accessType: 'search',
          filterCriteria: {
            region: selectedRegion !== 'all' ? selectedRegion : undefined,
            state: selectedState !== 'all' ? selectedState : undefined,
            type: selectedType !== 'all' ? selectedType : undefined,
            company: selectedCompany !== 'all' ? selectedCompany : undefined,
            searchName: searchName.trim() || undefined
          }
        });
      };
      
      // Debounce the logging to avoid excessive logs
      const timeoutId = setTimeout(logSearch, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [contacts, selectedRegion, selectedState, selectedType, selectedCompany, searchName, filteredContacts.length]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      
      console.log("[CONTACTS] 🔍 Iniciando fetchContacts");
      console.log("[CONTACTS] 👤 Role:", role);
      console.log("[CONTACTS] 👤 isRsvp:", isRsvp);
      console.log("[CONTACTS] 👤 isAdmin:", isAdmin);
      console.log("[CONTACTS] 👤 isEditor:", isEditor);
      console.log("[CONTACTS] 👤 isCliente:", isCliente);
      console.log("[CONTACTS] ⏳ roleLoading:", roleLoading);
      
      // Verify authentication
      const { data: { user } } = await supabase.auth.getUser();
      console.log("[CONTACTS] 👤 User ID:", user?.id);
      console.log("[CONTACTS] 📧 User Email:", user?.email);
      
      if (!user) {
        const currentPath = `${location.pathname}${location.search}`;
        navigate("/login", { state: { from: currentPath } });
        return;
      }

      console.log("[CONTACTS] 📡 Buscando contatos do banco...");
      const { data, error } = await supabase
        .from("contacts")
        .select("*")
        .order("company", { ascending: true });

      console.log("[CONTACTS] 📊 Dados retornados:", data?.length || 0, "contatos");
      console.log("[CONTACTS] ❌ Error:", error);

      if (error) {
        console.error("[CONTACTS] ❌ Erro na query:", error);
        // Check if it's a permission error
        if (error.code === 'PGRST301' || error.message?.includes('policy') || error.message?.includes('permission')) {
          toast({
            title: "Acesso negado",
            description: "Você não tem permissão para visualizar contatos. Apenas administradores, editores e clientes podem acessar.",
            variant: "destructive",
          });
          return;
        }
        throw error;
      }

      let mappedContacts: Contact[] = (data || []).map((item) => ({
        id: item.id,
        company: item.company,
        firstName: item.first_name,
        lastName: item.last_name,
        position: item.position || "",
        cellPhone: item.cell_phone || "",
        phone: item.phone || "",
        email: item.email || "",
        website: item.website || "",
        city: item.city || "",
        state: item.state || "",
        entityType: item.entity_type as "restaurant" | "operator" | "consolidator",
        region: item.region || "",
        source: (item.source as 'manual' | 'form' | 'csv') || 'manual',
        postalCode: item.postal_code || undefined,
        country: item.country || undefined,
        addressLine1: item.address_line_1 || undefined,
        addressLine2: item.address_line_2 || undefined,
        externalId: item.external_id || undefined,
        anonymousId: item.anonymous_id || undefined,
      }));

      console.log("[CONTACTS] 📊 Contatos mapeados:", mappedContacts.length);
      console.log("[CONTACTS] 📋 Contatos com source='form':", mappedContacts.filter(c => c.source === 'form').length);
      console.log("[CONTACTS] 📋 Sources disponíveis:", [...new Set(mappedContacts.map(c => c.source))]);

      // RSVP users only see contacts from forms
      if (isRsvp) {
        console.log("[CONTACTS] 🔒 Aplicando filtro RSVP (source='form')");
        const beforeFilter = mappedContacts.length;
        mappedContacts = mappedContacts.filter(c => c.source === 'form');
        console.log("[CONTACTS] 🔒 Filtrado:", beforeFilter, "→", mappedContacts.length);
      }

      console.log("[CONTACTS] ✅ Contatos finais:", mappedContacts.length);
      setContacts(mappedContacts);
      
      // Log contact access for security monitoring
      const { logContactAccess } = await import('@/utils/contactAccessLogger');
      logContactAccess({
        contactCount: mappedContacts.length,
        accessType: 'view',
        filterCriteria: {}
      });
    } catch (error: any) {
      console.error("[Contacts] Error fetching contacts:", error);
      toast({
        title: "Erro ao carregar contatos",
        description: error.message || "Não foi possível carregar os contatos.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filterContacts = () => {
    let filtered = [...contacts];

    // Busca por nome (firstName, lastName ou company)
    if (searchName.trim()) {
      const searchLower = searchName.toLowerCase();
      filtered = filtered.filter((c) => 
        (c.firstName || '').toLowerCase().includes(searchLower) ||
        (c.lastName || '').toLowerCase().includes(searchLower) ||
        (c.company || '').toLowerCase().includes(searchLower)
      );
    }

    if (selectedRegion !== "all") {
      filtered = filtered.filter((c) => c.region === selectedRegion);
    }

    if (selectedState !== "all") {
      filtered = filtered.filter((c) => c.state === selectedState);
    }

    if (selectedType !== "all") {
      if (selectedType === "rsvp") {
        filtered = filtered.filter((c) => c.source === "form");
      } else {
        filtered = filtered.filter((c) => c.entityType === selectedType);
      }
    }

    if (selectedCompany !== "all") {
      // Usa comparação fuzzy para encontrar contatos mesmo com pequenas variações no nome
      filtered = filtered.filter((c) => areNamesEquivalent(c.company, selectedCompany));
    }

    setFilteredContacts(filtered);
  };

  // Paginação
  const totalPages = Math.ceil(filteredContacts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedContacts = filteredContacts.slice(startIndex, endIndex);

  // Reset página ao filtrar
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedRegion, selectedState, selectedType, selectedCompany, searchName]);

  const handleImportCategory = (category: 'restaurant' | 'operator' | 'consolidator') => {
    setImportCategory(category);
    fileInputRef.current?.click();
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const csvContent = e.target?.result as string;
        
        // Detecta headers do CSV
        const preview = Papa.parse(csvContent, { 
          preview: 1,
          header: true 
        });
        
        const headers = Object.keys(preview.data[0] || {});
        
        if (headers.length === 0) {
          throw new Error('CSV não possui cabeçalhos válidos');
        }
        
        // Armazena para processar depois do mapeamento
        setPendingCsvContent(csvContent);
        setCsvHeaders(headers);
        setShowColumnMapper(true);
        
      } catch (error) {
        console.error("Error reading CSV:", error);
        toast({
          title: "Erro ao ler CSV",
          description: "Verifique se o arquivo está formatado corretamente.",
          variant: "destructive",
        });
      }
    };

    reader.readAsText(file);
    event.target.value = "";
  };

  const handleConfirmMapping = async (mapping: FieldMapping) => {
    setShowColumnMapper(false);
    setImportLoading(true);
    setImportProgress(0);
    setImportStatus("Processando CSV com mapeamento...");

    try {
      const parsedContacts = parseContactsCSVWithMapping(
        pendingCsvContent, 
        mapping, 
        importCategory
      );

      setImportProgress(40);
      setImportStatus("Verificando duplicados...");

      const duplicateCheck = await checkContactDuplicates(parsedContacts);
      
      setImportLoading(false);
      setImportProgress(0);
      setImportStatus("");

      setPendingImport(duplicateCheck);
      setShowImportDialog(true);

    } catch (error) {
      console.error("Error importing contacts:", error);
      setImportLoading(false);
      toast({
        title: "Erro na importação",
        description: "Não foi possível importar os contatos.",
        variant: "destructive",
      });
    }
  };

  const handleConfirmImport = async (mode: ImportMode) => {
    if (mode === 'cancel' || !pendingImport) {
      setShowImportDialog(false);
      setPendingImport(null);
      return;
    }

    setShowImportDialog(false);
    setImportLoading(true);
    setImportProgress(0);

    try {
      let contactsToProcess: Contact[] = [];

      if (mode === 'new-only') {
        contactsToProcess = pendingImport.new;
        setImportStatus(`Inserindo ${contactsToProcess.length} novos contatos...`);
      } else if (mode === 'upsert') {
        contactsToProcess = [...pendingImport.new, ...pendingImport.existing];
        setImportStatus(`Processando ${contactsToProcess.length} contatos...`);
      }

      setImportProgress(30);

      // Separa inserções e atualizações
      const toInsert = mode === 'new-only' 
        ? pendingImport.new 
        : pendingImport.new;
      
      const toUpdate = mode === 'upsert' 
        ? pendingImport.existing 
        : [];

      // Insere novos
      if (toInsert.length > 0) {
        const dbRecords = toInsert.map(contact => ({
          company: contact.company,
          first_name: contact.firstName,
          last_name: contact.lastName,
          position: contact.position,
          cell_phone: contact.cellPhone,
          phone: contact.phone,
          email: contact.email,
          website: contact.website,
          city: contact.city,
          state: contact.state,
          entity_type: contact.entityType,
          region: contact.region,
          postal_code: contact.postalCode,
          country: contact.country,
          address_line_1: contact.addressLine1,
          address_line_2: contact.addressLine2,
          external_id: contact.externalId,
          anonymous_id: contact.anonymousId,
        }));

        const { error } = await supabase.from("contacts").insert(dbRecords);
        if (error) throw error;
      }

      setImportProgress(60);

      // Atualiza existentes
      if (toUpdate.length > 0) {
        for (const contact of toUpdate) {
          const { error } = await supabase
            .from("contacts")
            .update({
              company: contact.company,
              first_name: contact.firstName,
              last_name: contact.lastName,
              position: contact.position,
              cell_phone: contact.cellPhone,
              phone: contact.phone,
              email: contact.email,
              website: contact.website,
              city: contact.city,
              state: contact.state,
              entity_type: contact.entityType,
              region: contact.region,
              postal_code: contact.postalCode,
              country: contact.country,
              address_line_1: contact.addressLine1,
              address_line_2: contact.addressLine2,
              external_id: contact.externalId,
              anonymous_id: contact.anonymousId,
            })
            .eq("id", contact.id);

          if (error) throw error;
        }
      }

      setImportProgress(80);
      setImportStatus("Recarregando dados...");

      await fetchContacts();

      setImportProgress(100);
      setImportStatus("Concluído!");
      
      // Record import metadata
      await recordImportMetadata({
        tableName: 'contacts',
        entityType: importCategory,
        updateType: 'csv_import',
        recordsAffected: toInsert.length + toUpdate.length
      });
      queryClient.invalidateQueries({ queryKey: ['lastUpdate', 'contacts'] });

      toast({
        title: "Importação concluída",
        description: `${toInsert.length} novo(s), ${toUpdate.length} atualizado(s).`,
      });

      setTimeout(() => {
        setImportLoading(false);
        setImportProgress(0);
        setImportStatus("");
        setPendingImport(null);
      }, 1000);

    } catch (error) {
      console.error("Error processing import:", error);
      setImportLoading(false);
      setImportProgress(0);
      setImportStatus("");
      toast({
        title: "Erro ao processar",
        description: "Não foi possível completar a importação.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadTemplate = () => {
    const template = generateContactsTemplate();
    downloadTemplate(template, 'template_contatos.csv');
    
    toast({
      title: "Template baixado",
      description: "O template CSV foi baixado com sucesso."
    });
  };

  const handleExport = async () => {
    // Log export activity for security monitoring
    const { logContactAccess } = await import('@/utils/contactAccessLogger');
    await logContactAccess({
      contactCount: filteredContacts.length,
      accessType: 'export',
      filterCriteria: {
        region: selectedRegion,
        state: selectedState,
        type: selectedType,
        company: selectedCompany,
        searchName
      }
    });
    
    const csv = exportContactsToCSV(filteredContacts);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `contatos_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: "Exportação concluída",
      description: "Contatos exportados com sucesso.",
    });
  };

  const handleUpdateContact = async (contact: Contact) => {
    try {
      const { error } = await supabase
        .from("contacts")
        .update({
          company: contact.company,
          first_name: contact.firstName,
          last_name: contact.lastName,
          position: contact.position,
          cell_phone: contact.cellPhone,
          phone: contact.phone,
          email: contact.email,
          website: contact.website,
          city: contact.city,
          state: contact.state,
          entity_type: contact.entityType,
          region: contact.region,
          postal_code: contact.postalCode,
          country: contact.country,
          address_line_1: contact.addressLine1,
          address_line_2: contact.addressLine2,
          external_id: contact.externalId,
          anonymous_id: contact.anonymousId,
        })
        .eq("id", contact.id);

      if (error) throw error;

      await fetchContacts();

      toast({
        title: "Contato atualizado",
        description: "As alterações foram salvas com sucesso.",
      });
    } catch (error) {
      console.error("Error updating contact:", error);
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível salvar as alterações.",
        variant: "destructive",
      });
    }
  };

  const handleAddContact = async (contact: Omit<Contact, 'id'>) => {
    try {
      const dbRecord = {
        company: contact.company,
        first_name: contact.firstName,
        last_name: contact.lastName,
        position: contact.position,
        cell_phone: contact.cellPhone,
        phone: contact.phone,
        email: contact.email,
        website: contact.website,
        city: contact.city,
        state: contact.state,
        entity_type: contact.entityType,
        region: contact.region,
        postal_code: contact.postalCode,
        country: contact.country,
        address_line_1: contact.addressLine1,
        address_line_2: contact.addressLine2,
        external_id: contact.externalId,
        anonymous_id: contact.anonymousId,
      };

      const { error } = await supabase.from("contacts").insert(dbRecord);

      if (error) throw error;
      
      // Record manual add metadata
      await recordImportMetadata({
        tableName: 'contacts',
        entityType: contact.entityType,
        updateType: 'manual_add',
        recordsAffected: 1
      });
      queryClient.invalidateQueries({ queryKey: ['lastUpdate', 'contacts'] });

      await fetchContacts();
    } catch (error) {
      console.error("Error adding contact:", error);
      throw error;
    }
  };

  const handleDeleteContact = async (contact: Contact) => {
    const { error } = await supabase
      .from('contacts')
      .delete()
      .eq('id', contact.id);
    
    if (error) {
      toast({
        title: "Erro ao deletar",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
    
    // Atualizar lista local
    setContacts(prev => prev.filter(c => c.id !== contact.id));
    
    toast({
      title: "Contato deletado",
      description: `${contact.firstName} ${contact.lastName} foi removido com sucesso.`,
    });
  };

  const handleClearAllContacts = async () => {
    try {
      console.log('=== INÍCIO DA LIMPEZA DE CONTATOS ===');
      console.log('[PASSO 1] Verificando autenticação...');
      
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      console.log('[PASSO 1] ✅ User ID:', user?.id);
      console.log('[PASSO 1] ✅ Email:', user?.email);
      
      if (authError || !user) {
        console.error('[PASSO 1] ❌ ERRO:', authError);
        throw new Error('Usuário não autenticado');
      }

      console.log('[PASSO 2] Verificando role do usuário...');
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .single();
      
      console.log('[PASSO 2] Role retornado:', roleData?.role);
      console.log('[PASSO 2] Erro ao buscar role?:', roleError);
      
      if (roleError) {
        console.error('[PASSO 2] ❌ ERRO ao buscar role:', roleError);
        throw new Error('Erro ao verificar permissões');
      }
      
      if (roleData?.role !== 'admin') {
        console.error('[PASSO 2] ❌ Usuário NÃO é admin. Role atual:', roleData?.role);
        throw new Error('Apenas administradores podem limpar contatos');
      }
      
      console.log('[PASSO 2] ✅ Usuário é ADMIN');

      console.log('[PASSO 3] Contando registros...');
      const { count: beforeCount, error: countError } = await supabase
        .from('contacts')
        .select('*', { count: 'exact', head: true });
      
      console.log('[PASSO 3] Total de contatos:', beforeCount);
      if (countError) console.error('[PASSO 3] ⚠️ Erro ao contar:', countError);

      console.log('[PASSO 4] EXECUTANDO DELETE...');
      console.log('[PASSO 4] Timestamp início:', new Date().toISOString());
      const startTime = Date.now();
      
      const { error: deleteError, count: deletedCount } = await supabase
        .from('contacts')
        .delete({ count: 'exact' })
        .gte('id', 0);

      const duration = Date.now() - startTime;
      console.log('[PASSO 4] Timestamp fim:', new Date().toISOString());
      console.log('[PASSO 4] Duração:', duration, 'ms');
      console.log('[PASSO 4] Registros deletados:', deletedCount);

      if (deleteError) {
        console.error('[PASSO 4] ❌ ERRO NO DELETE:');
        console.error('  - Code:', deleteError.code);
        console.error('  - Message:', deleteError.message);
        console.error('  - Details:', deleteError.details);
        console.error('  - Hint:', deleteError.hint);
        throw deleteError;
      }

      console.log('[PASSO 4] ✅ DELETE executado com sucesso');
      console.log('[PASSO 5] Atualizando estado da aplicação...');

      setContacts([]);
      setFilteredContacts([]);
      setSelectedRegion("all");
      setSelectedState("all");
      setSelectedType("all");
      setSelectedCompany("all");

      console.log('[PASSO 5] ✅ Estado atualizado');
      console.log('=== LIMPEZA CONCLUÍDA COM SUCESSO ===');

      toast({
        title: "Contatos limpos",
        description: `${deletedCount || beforeCount || 0} contatos foram removidos.`,
      });
    } catch (error) {
      console.error('=== ERRO FATAL ===');
      console.error('Tipo:', error);
      console.error('Message:', error instanceof Error ? error.message : 'Desconhecido');
      console.error('Stack:', error instanceof Error ? error.stack : 'N/A');
      
      toast({
        title: "Erro ao limpar contatos",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar onClearData={() => {}} onClearContacts={handleClearAllContacts} />
        
        <main className="flex-1">
          <div className="container mx-auto p-6 space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                  Contatos
                </h1>
                <p className="text-muted-foreground mt-2">
                  Gerencie os contatos das empresas cadastradas
                </p>
              </div>

              <div className="flex gap-2">
                {canEdit && !isRsvp && (
                  <Button onClick={() => setShowAddDialog(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Adicionar Contato
                  </Button>
                )}
                {!isRsvp && (
                  <Button variant="outline" onClick={handleDownloadTemplate}>
                    <FileText className="mr-2 h-4 w-4" />
                    Baixar Template
                  </Button>
                )}
                <Button variant="outline" onClick={handleExport}>
                  <Download className="mr-2 h-4 w-4" />
                  Exportar CSV
                </Button>
                {canEdit && !isRsvp && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="default" disabled={importLoading}>
                        <Upload className="mr-2 h-4 w-4" />
                        Importar CSV
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => handleImportCategory('restaurant')}>
                        🍽️ Restaurantes
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleImportCategory('operator')}>
                        🏢 Operadoras
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleImportCategory('consolidator')}>
                        🏗️ Consolidadoras
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleImport}
                className="hidden"
                disabled={importLoading}
              />
            </div>

            {importLoading && (
              <div className="p-6 border border-border rounded-lg bg-card space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    <div>
                      <p className="font-medium text-foreground">Importando contatos</p>
                      <p className="text-sm text-muted-foreground">{importStatus}</p>
                    </div>
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">{importProgress}%</span>
                </div>
                <Progress value={importProgress} className="h-2" />
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-3 text-muted-foreground">Carregando contatos...</span>
              </div>
            ) : (
              <>
                <div className="mb-4 flex justify-end items-center">
                  <LastUpdateInfo tableName="contacts" />
                </div>
                
                {filteredContacts.length > 0 && (
                  <div className="flex items-center justify-between bg-muted/30 p-4 rounded-lg border mb-6">
                    <div className="text-sm text-muted-foreground">
                      Mostrando {startIndex + 1}-{Math.min(endIndex, filteredContacts.length)} de {filteredContacts.length} contatos
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Anterior
                      </Button>
                      
                      <div className="text-sm">
                        Página {currentPage} de {totalPages}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Próxima
                      </Button>
                    </div>
                  </div>
                )}
                
                <ContactDashboard
                  contacts={paginatedContacts}
                  allContacts={contacts}
                  selectedRegion={selectedRegion}
                  selectedState={selectedState}
                  selectedType={selectedType}
                  selectedCompany={selectedCompany}
                  searchName={searchName}
                  onRegionChange={setSelectedRegion}
                  onStateChange={setSelectedState}
                  onTypeChange={setSelectedType}
                  onCompanyChange={setSelectedCompany}
                  onSearchNameChange={setSearchName}
                  onUpdateContact={handleUpdateContact}
                  onDeleteContact={handleDeleteContact}
                />
                
                {filteredContacts.length > 0 && (
                  <div className="flex items-center justify-between bg-muted/30 p-4 rounded-lg border mt-6">
                    <div className="text-sm text-muted-foreground">
                      Mostrando {startIndex + 1}-{Math.min(endIndex, filteredContacts.length)} de {filteredContacts.length} contatos
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Anterior
                      </Button>
                      
                      <div className="text-sm">
                        Página {currentPage} de {totalPages}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Próxima
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>

        {/* Dialogs */}
        <ColumnMapperDialog
          open={showColumnMapper}
          onOpenChange={setShowColumnMapper}
          csvHeaders={csvHeaders}
          onConfirm={handleConfirmMapping}
          type="contacts"
        />

        <ImportConfirmDialog
          open={showImportDialog}
          onOpenChange={setShowImportDialog}
          newItems={pendingImport?.new || []}
          existingItems={pendingImport?.existing || []}
          duplicates={pendingImport?.duplicates || []}
          onConfirm={handleConfirmImport}
          entityName="contato"
        />

        <AddContactDialog
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
          onAdd={handleAddContact}
        />
      </div>
    </SidebarProvider>
  );
};

export default Contacts;
